window.BreederFamilyTreeVisualization = class BreederFamilyTreeVisualization {
    constructor(containerId, treeData, breederStrainIds, translations, breederId) {
        this.container = document.getElementById(containerId);
        this.treeData = treeData;
        this.breederStrainIdsSet = new Set(breederStrainIds || []);
        this.translations = translations;
        this.breederId = breederId;
        this.network = null;
        this.data = null;
        this.edgesMap = {};
        this.childrenMap = {};
        this.currentCenterId = breederId;
        this.circlesDrawn = false;
        this.maxOrbitLevel = 0;
        this.orbitalDistance = 130;
        this.baseOrbitRadius = 100;
        this.nodeSpacing = 15;
        this.orbitRadii = {};
        this.animationDuration = 800;
        this.previousNodeStyles = {};
        this.previousEdgeStyles = {};
        
        if (!this.container) return;
        
        this.init();
    }
    
    updateBatch(items, updateFn) {
        const batchSize = 50;
        for (let i = 0; i < items.length; i += batchSize) {
            const batch = items.slice(i, i + batchSize);
            updateFn(batch);
        }
    }
    
    easeInOutCubic(t) {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }
    
    init() {
        this.buildEdgesMap();
        this.calculateMaxOrbitLevel();
        this.createNetwork();
        this.initSearch();
    }
    
    calculateMaxOrbitLevel() {
        const treeStructure = this.buildTreeStructure(this.breederId);
        const nodeLevelMap = {};
        let maxLevel = 0;
        
        const collectNodes = (node, distanceFromRoot = 0) => {
            if (!nodeLevelMap[node.id] || distanceFromRoot < nodeLevelMap[node.id]) {
                nodeLevelMap[node.id] = distanceFromRoot;
                if (distanceFromRoot > maxLevel) {
                    maxLevel = distanceFromRoot;
                }
            }
            
            node.parents.forEach(parent => collectNodes(parent, distanceFromRoot + 1));
            node.children.forEach(child => collectNodes(child, distanceFromRoot + 1));
        };
        
        collectNodes(treeStructure, 0);
        this.maxOrbitLevel = maxLevel || 1;
    }
    
    getParentIds(nodeId) {
        return this.edgesMap[nodeId] || [];
    }

    getChildIds(nodeId) {
        return this.childrenMap[nodeId] || [];
    }
    
    getCanvas() {
        let canvas = document.getElementById('background-canvas');
        if (!canvas && this.container) {
            canvas = this.container.querySelector('#background-canvas');
        }
        return canvas;
    }
    
    getCircleCenter() {
        const width = this.container.offsetWidth || this.container.clientWidth;
        const height = this.container.offsetHeight || this.container.clientHeight;
        let centerX = width / 2;
        let centerY = height / 2;
        let scale = 1;
        
        if (this.network && this.currentCenterId) {
            try {
                scale = this.network.getScale();
                const viewPosition = this.network.getViewPosition();
                const positions = this.network.getPositions([this.currentCenterId]);
                const centerNodePos = positions[this.currentCenterId];
                
                if (centerNodePos) {
                    const offsetX = centerNodePos.x - viewPosition.x;
                    const offsetY = centerNodePos.y - viewPosition.y;
                    centerX = offsetX * scale + width / 2;
                    centerY = offsetY * scale + height / 2;
                }
            } catch (e) {}
        }
        
        return { centerX, centerY, scale, width, height };
    }
    
    drawBackgroundCircles(force = false) {
        if (!force && this.circlesDrawn) return;
        
        let canvas = this.getCanvas();
        if (!canvas && this.container) {
            canvas = document.createElement('canvas');
            canvas.id = 'background-canvas';
            canvas.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 0; opacity: 0.8;';
            this.container.insertBefore(canvas, this.container.firstChild);
        }
        if (!canvas) return;
        
        const { centerX, centerY, scale, width, height } = this.getCircleCenter();
        
        if (width === 0 || height === 0) return;
        
        const maxLevel = Math.max(this.maxOrbitLevel || 1, 1);
        if (maxLevel === 0) return;
        
        canvas.width = width;
        canvas.height = height;
        canvas.style.width = width + 'px';
        canvas.style.height = height + 'px';
        
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, width, height);
        ctx.strokeStyle = '#3f6144';
        ctx.lineWidth = 2;
        
        for (let i = 1; i <= maxLevel; i++) {
            const radiusInNetworkCoords = this.orbitRadii[i] || (i * this.orbitalDistance);
            const radiusInCanvasCoords = radiusInNetworkCoords * scale;
            
            ctx.beginPath();
            ctx.arc(centerX, centerY, radiusInCanvasCoords, 0, Math.PI * 2, true);
            ctx.stroke();
            ctx.closePath();
        }
        
        this.circlesDrawn = true;
    }
    
    buildEdgesMap() {
        this.treeData.edges.forEach(edge => {
            if (!this.edgesMap[edge.to]) this.edgesMap[edge.to] = [];
            this.edgesMap[edge.to].push(edge.from);

            if (!this.childrenMap[edge.from]) this.childrenMap[edge.from] = [];
            this.childrenMap[edge.from].push(edge.to);
        });
    }
    
    createNetwork() {
        this.data = {
            nodes: new vis.DataSet(this.treeData.nodes),
            edges: new vis.DataSet(this.treeData.edges),
        };
        
        const options = {
            layout: {
                improvedLayout: false,
            },
            physics: {
                enabled: false,
            },
            interaction: {
                dragNodes: false,
                dragView: true,
                zoomView: true,
                navigationButtons: false,
            },
            nodes: {
                shape: 'dot',
                font: {
                    size: 16,
                    face: 'Arial',
                },
                scaling: {
                    min: 4,
                    max: 18,
                },
                margin: 10,
            },
            edges: {
                arrows: {
                    to: {
                        enabled: true,
                        scaleFactor: 1.2,
                    }
                },
                smooth: {
                    type: 'curvedCW',
                    roundness: 0.3,
                },
            },
        };
        
        const containerSize = this.container.getBoundingClientRect();
        const centerX = containerSize.width / 2;
        const centerY = containerSize.height / 2;
        
        const treeStructure = this.buildTreeStructure(this.breederId);
        this.updateStylesByLevel(treeStructure);
        const targetPositions = this.calculateOrbitalPositions(treeStructure, centerX, centerY);
        
        const allNodes = this.data.nodes.get();
        const randomPositions = {};
        allNodes.forEach(node => {
            if (node.id === this.breederId) {
                randomPositions[node.id] = { x: centerX, y: centerY };
            } else {
                const angle = Math.random() * Math.PI * 2;
                const radius = 50 + Math.random() * 200;
                randomPositions[node.id] = {
                    x: centerX + radius * Math.cos(angle),
                    y: centerY + radius * Math.sin(angle),
                };
            }
        });
        
        const randomUpdates = Object.keys(randomPositions).map(id => ({
            id: id,
            x: randomPositions[id].x,
            y: randomPositions[id].y,
            fixed: id === this.breederId,
        }));
        
        this.data.nodes.update(randomUpdates);
        
        this.network = new vis.Network(this.container, this.data, options);
        this.setupEventHandlers();
        this.setupCircleRedrawHandlers();
        
        requestAnimationFrame(() => {
            this.circlesDrawn = false;
            this.drawBackgroundCircles(true);
        });
        
        this.network.once('afterDrawing', () => {
            this.animateNodesToPositions(randomPositions, targetPositions, 1200, () => {
                this.circlesDrawn = false;
                this.drawBackgroundCircles(true);
                this.centerViewport(true);
            });
        });
    }
    
    setupEventHandlers() {
        this.network.on('click', (params) => {
            if (params.nodes.length > 0) {
                const nodeId = params.nodes[0];
                if (nodeId === this.breederId) return;
                this.centerOnNode(nodeId, false);
            }
        });
        
        this.network.on('doubleClick', (params) => {
            if (params.nodes.length > 0) {
                const node = this.data.nodes.get(params.nodes[0]);
                if (node?.url) {
                    window.location.href = node.url;
                }
            }
        });
    }
    
    setupCircleRedrawHandlers() {
        let isDragging = false;
        let animationFrameId = null;
        
        const redraw = () => {
            if (animationFrameId !== null) {
                cancelAnimationFrame(animationFrameId);
            }
            animationFrameId = requestAnimationFrame(() => {
                this.circlesDrawn = false;
                this.drawBackgroundCircles(true);
                animationFrameId = null;
                if (isDragging) {
                    redraw();
                }
            });
        };
        
        this.network.on('dragStart', () => {
            isDragging = true;
            redraw();
        });
        
        this.network.on('drag', () => {
            if (!isDragging) isDragging = true;
            redraw();
        });
        
        this.network.on('dragEnd', () => {
            isDragging = false;
            if (animationFrameId !== null) {
                cancelAnimationFrame(animationFrameId);
                animationFrameId = null;
            }
            this.circlesDrawn = false;
            this.drawBackgroundCircles(true);
        });
        
        let resizeAnimationFrame;
        window.addEventListener('resize', () => {
            if (resizeAnimationFrame) {
                cancelAnimationFrame(resizeAnimationFrame);
            }
            resizeAnimationFrame = requestAnimationFrame(() => {
                this.circlesDrawn = false;
                this.drawBackgroundCircles(true);
            });
        });
        
        let zoomAnimationFrame;
        this.network.on('zoom', () => {
            if (zoomAnimationFrame) {
                cancelAnimationFrame(zoomAnimationFrame);
            }
            zoomAnimationFrame = requestAnimationFrame(() => {
                this.circlesDrawn = false;
                this.drawBackgroundCircles(true);
            });
        });
    }
    
    animateNodesToPositions(startPositions, targetPositions, duration, onComplete) {
        const startTime = Date.now();
        
        const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = this.easeInOutCubic(progress);
            
            const updates = Object.keys(targetPositions).map(id => {
                const targetPos = targetPositions[id];
                const startPos = startPositions[id] || targetPos;
                return {
                    id: id,
                    x: startPos.x + (targetPos.x - startPos.x) * eased,
                    y: startPos.y + (targetPos.y - startPos.y) * eased,
                };
            });
            
            this.data.nodes.update(updates);
            this.circlesDrawn = false;
            this.drawBackgroundCircles(true);
            
            if (progress < 1) {
                requestAnimationFrame(animate);
            } else if (onComplete) {
                onComplete();
            }
        };
        
        requestAnimationFrame(animate);
    }
    
    initSearch() {
        const searchInput = document.getElementById('strain-search');
        const searchBtn = document.getElementById('search-btn');
        const searchResult = document.getElementById('search-result');
        
        if (!searchInput || !searchBtn || !searchResult) return;
        
        searchInput.placeholder = this.translations.searchPlaceholder || 'Search strain by name...';
        searchBtn.textContent = this.translations.search || 'Search';
        
        const performSearch = () => {
            const query = searchInput.value.trim().toLowerCase();
            if (!query) {
                searchResult.textContent = '';
                searchResult.style.display = 'none';
                return;
            }
            
            searchResult.style.display = 'block';

            const foundNode = this.treeData.nodes.find(node =>
                node.label && node.label.toLowerCase().includes(query)
            );
            
            if (foundNode) {
                const foundText = this.translations.found || 'Found';
                searchResult.textContent = `${foundText}: ${foundNode.label}`;
                searchResult.className = 'block mt-2 text-sm break-words text-green-600';
                this.focusOnNode(foundNode.id);
            } else {
                searchResult.textContent = this.translations.notFound || 'Strain not found';
                searchResult.className = 'block mt-2 text-sm break-words text-red-600';
            }
        };
        
        searchBtn.addEventListener('click', performSearch);
        searchInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                performSearch();
            }
        });
    }
    
    focusOnNode(nodeId, initialScale = 1.5) {
        if (!this.network) return;
        
        const node = this.data.nodes.get(nodeId);
        if (!node) return;
        
        this.centerOnNode(nodeId, true);
        
        requestAnimationFrame(() => {
            const positions = this.network.getPositions([nodeId]);
            if (positions[nodeId]) {
                this.network.focus(nodeId, {
                    scale: initialScale,
                    animation: {
                        duration: 500,
                        easingFunction: 'easeInOutQuad'
                    }
                });
            }
        });
    }
    
    centerOnNode(nodeId, isInitial = false) {
        const node = this.data.nodes.get(nodeId);
        if (!node) return;
        
        const childIds = this.getChildIds(nodeId);
        
        if (childIds.length === 0 && nodeId !== this.breederId && !isInitial) {
            const parentIds = this.edgesMap[nodeId] || [];
            this.updateDetails(node, parentIds);
            this.centerOnNode(this.breederId, false);
            return;
        }
        
        this.currentCenterId = nodeId;
        
        const containerSize = this.container.getBoundingClientRect();
        const centerX = containerSize.width / 2;
        const centerY = containerSize.height / 2;
        
        const treeStructure = this.buildTreeStructure(nodeId);
        const nodeCount = this.countNodesInStructure(treeStructure);
        
        if (nodeCount <= 1 && nodeId !== this.breederId && !isInitial) {
            const parentIds = this.edgesMap[nodeId] || [];
            this.updateDetails(node, parentIds);
            this.centerOnNode(this.breederId, false);
            return;
        }
        
        this.updateStylesByLevel(treeStructure);
        const positions = this.calculateOrbitalPositions(treeStructure, centerX, centerY);
        
        if (nodeId === this.breederId) {
            const allNodes = this.data.nodes.get();
            allNodes.forEach(node => {
                if (!positions[node.id] && node.id !== this.breederId) {
                    const nodeLevel = node.level || 0;
                    if (nodeLevel >= 0) {
                        const radius = this.orbitRadii[nodeLevel] || (this.baseOrbitRadius + (nodeLevel - 1) * this.orbitalDistance);
                        const angle = Math.random() * Math.PI * 2;
                        positions[node.id] = {
                            x: centerX + radius * Math.cos(angle),
                            y: centerY + radius * Math.sin(angle),
                        };
                    }
                }
            });
        }
        
        const currentPositions = this.network.getPositions();
        const updates = Object.keys(positions).map(id => ({
            id: id,
            x: positions[id].x,
            y: positions[id].y,
            fixed: id === nodeId,
        }));
        
        if (Object.keys(currentPositions).length > 0 && !isInitial) {
            this.animateNodesToPositions(currentPositions, positions, this.animationDuration, () => {
                this.data.nodes.update(updates);
                this.circlesDrawn = false;
                this.drawBackgroundCircles(true);
            });
        } else {
            this.data.nodes.update(updates);
            this.circlesDrawn = false;
            this.drawBackgroundCircles(true);
        }
        
        const parentIds = this.edgesMap[nodeId] || [];
        this.updateDetails(node, parentIds);
    }
    
    countNodesInStructure(structure) {
        const visited = new Set();
        let count = 0;
        
        const traverse = (node) => {
            if (visited.has(node.id)) return;
            visited.add(node.id);
            count++;
            
            node.parents.forEach(parent => traverse(parent));
            node.children.forEach(child => traverse(child));
        };
        
        traverse(structure);
        return count;
    }
    
    centerViewport(isInitial = false) {
        const centralNodeId = this.currentCenterId;
        if (!centralNodeId) return;
        
        if (isInitial) {
            const animationDuration = 800;
            const startTime = Date.now();
            
            const redrawOrbits = () => {
                const canvas = this.getCanvas();
                if (!canvas) return;
                
                this.circlesDrawn = false;
                this.drawBackgroundCircles(true);
                
                const elapsed = Date.now() - startTime;
                if (elapsed < animationDuration) {
                    requestAnimationFrame(redrawOrbits);
                }
            };
            
            const currentScale = this.network.getScale();
            this.network.focus(centralNodeId, {
                scale: currentScale,
                animation: {
                    duration: animationDuration,
                    easingFunction: 'easeInOutQuad',
                },
            });
            
            requestAnimationFrame(redrawOrbits);
        } else {
            const currentScale = this.network.getScale();
            this.network.focus(centralNodeId, {
                scale: currentScale,
                animation: false,
            });
        }
    }
    
    buildTreeStructure(rootId) {
        const structure = {
            id: rootId,
            level: 0,
            parents: [],
            children: [],
        };
        
        const visited = new Set([rootId]);
        const queue = [structure];
        let qi = 0;

        while (qi < queue.length) {
            const current = queue[qi++];

            for (const parentId of (this.edgesMap[current.id] || [])) {
                if (parentId === this.breederId && current.id !== this.breederId) continue;
                if (!visited.has(parentId)) {
                    visited.add(parentId);
                    const parentNode = { id: parentId, level: current.level + 1, parents: [], children: [] };
                    current.parents.push(parentNode);
                    queue.push(parentNode);
                }
            }

            for (const childId of (this.childrenMap[current.id] || [])) {
                if (!visited.has(childId)) {
                    visited.add(childId);
                    const childNode = { id: childId, level: current.level - 1, parents: [], children: [] };
                    current.children.push(childNode);
                    queue.push(childNode);
                }
            }
        }
        
        if (rootId === this.breederId) {
            const allNodes = this.data ? this.data.nodes.get() : this.treeData.nodes;
            if (!allNodes) {
                return structure;
            }
            
            const nodesInStructure = new Set();
            const collectNodeIds = (node) => {
                nodesInStructure.add(node.id);
                node.parents.forEach(p => collectNodeIds(p));
                node.children.forEach(c => collectNodeIds(c));
            };
            collectNodeIds(structure);
            
            allNodes.forEach(node => {
                if (node.id !== this.breederId && !nodesInStructure.has(node.id)) {
                    const parentIds = this.getParentIds(node.id);
                    if (parentIds.includes(this.breederId)) {
                        structure.children.push({
                            id: node.id,
                            level: -1,
                            parents: [],
                            children: [],
                        });
                    }
                }
            });
        }
        
        return structure;
    }
    
    calculateOrbitalPositions(treeStructure, centerX, centerY) {
        const positions = {};
        positions[treeStructure.id] = { x: centerX, y: centerY };
        
        const nodesByLevel = {};
        const nodeLevelMap = {};
        let maxLevel = 0;
        
        const collectNodes = (node, level, distanceFromRoot = 0) => {
            if (!nodeLevelMap[node.id] || distanceFromRoot < nodeLevelMap[node.id].absLevel) {
                if (nodeLevelMap[node.id] !== undefined) {
                    const prevLevel = nodeLevelMap[node.id].absLevel;
                    if (nodesByLevel[prevLevel]) {
                        const idx = nodesByLevel[prevLevel].indexOf(node.id);
                        if (idx !== -1) {
                            nodesByLevel[prevLevel].splice(idx, 1);
                        }
                    }
                }
                
                nodeLevelMap[node.id] = {
                    absLevel: distanceFromRoot,
                    originalLevel: level,
                    isParent: level > 0,
                    isChild: level < 0
                };
                
                if (distanceFromRoot > maxLevel) {
                    maxLevel = distanceFromRoot;
                }
                
                if (!nodesByLevel[distanceFromRoot]) {
                    nodesByLevel[distanceFromRoot] = [];
                }
                if (node.id !== treeStructure.id && !nodesByLevel[distanceFromRoot].includes(node.id)) {
                    nodesByLevel[distanceFromRoot].push(node.id);
                }
            }
            
            node.parents.forEach(parent => collectNodes(parent, level + 1, distanceFromRoot + 1));
            node.children.forEach(child => collectNodes(child, level - 1, distanceFromRoot + 1));
        };
        collectNodes(treeStructure, 0, 0);
        
        this.maxOrbitLevel = maxLevel;
        
        this.orbitRadii = {};
        let previousMaxRadius = 0;
        
        Object.keys(nodesByLevel).sort((a, b) => parseInt(a) - parseInt(b)).forEach(level => {
            const levelNum = parseInt(level);
            if (levelNum === 0) return;
            
            const nodes = nodesByLevel[levelNum];
            if (nodes.length === 0) return;
            
            const nodeCount = nodes.length;
            const minRadius = levelNum === 1 
                ? this.baseOrbitRadius 
                : previousMaxRadius + this.orbitalDistance;
            const nodeBasedRadius = (nodeCount * this.nodeSpacing) / (2 * Math.PI);
            const radius = minRadius + nodeBasedRadius;
            
            this.orbitRadii[levelNum] = radius;
            previousMaxRadius = radius;
            
            const angleStep = (2 * Math.PI) / nodeCount;
            
            nodes.forEach((nodeId, index) => {
                const baseAngle = index * angleStep;
                const angleOffset = (Math.random() - 0.5) * 0.2 * angleStep;
                const angle = baseAngle + angleOffset;
                
                positions[nodeId] = {
                    x: centerX + radius * Math.cos(angle),
                    y: centerY + radius * Math.sin(angle),
                };
            });
        });
        
        return positions;
    }
    
    updateStylesByLevel(treeStructure) {
        const nodeLevels = {};
        const collectLevels = (node, level) => {
            const absLevel = Math.abs(level);
            if (!nodeLevels[node.id] || absLevel < nodeLevels[node.id]) {
                nodeLevels[node.id] = absLevel;
            }
            node.parents.forEach(parent => collectLevels(parent, level + 1));
            node.children.forEach(child => collectLevels(child, level - 1));
        };
        collectLevels(treeStructure, 0);
        
        const allNodes = this.data.nodes.get();
        const nodeUpdates = [];
        
        allNodes.forEach(node => {
            const absLevel = nodeLevels[node.id] ?? 999;
            const isCenter = node.id === treeStructure.id;
            const isOwnStrain = this.breederStrainIdsSet.has(node.id);
            const isBreeder = node.id === this.breederId;
            
            let size, fontSize, fontColor, bgColor, borderColor;
            
            if (isBreeder) {
                size = 0;
                fontSize = 0;
                fontColor = 'transparent';
                bgColor = 'transparent';
                borderColor = 'transparent';
            } else if (isOwnStrain) {
                size = 14;
                fontSize = 15;
                fontColor = '#ffff66';
                bgColor = '#ffff66';
                borderColor = '#ffff66';
            } else if (isCenter) {
                size = 18;
                fontSize = 16;
                fontColor = '#ffff80';
                bgColor = '#ffff80';
                borderColor = '#ffff80';
            } else {
                size = Math.max(6, 10 - absLevel);
                fontSize = Math.max(10, 12 - absLevel);
                fontColor = '#96b18d';
                bgColor = '#a3b1a0';
                borderColor = '#96b18d';
            }
            
            const styleKey = `${node.id}_${size}_${fontSize}_${bgColor}_${isOwnStrain}_${isBreeder}`;
            if (this.previousNodeStyles[node.id] !== styleKey) {
                nodeUpdates.push({
                    id: node.id,
                    size: size,
                    font: { 
                        size: fontSize, 
                        color: fontColor,
                        face: 'Arial',
                        bold: isOwnStrain
                    },
                    color: {
                        background: bgColor,
                        border: borderColor,
                        highlight: '#ffff66',
                    },
                });
                this.previousNodeStyles[node.id] = styleKey;
            }
        });
        
        if (nodeUpdates.length > 0) {
            this.updateBatch(nodeUpdates, (batch) => this.data.nodes.update(batch));
        }
        
        const allEdges = this.data.edges.get();
        const edgeUpdates = [];
        
        allEdges.forEach(edge => {
            const fromLevel = nodeLevels[edge.from] ?? 999;
            const toLevel = nodeLevels[edge.to] ?? 999;
            const minLevel = Math.min(fromLevel, toLevel);
            
            let width, color;
            if (edge.from === this.breederId || edge.to === this.breederId) {
                width = 0.5;
                color = '#ccff99';
            } else if (minLevel === 0) {
                width = 1.2;
                color = '#5a6555';
            } else if (minLevel === 1) {
                width = 0.5;
                color = '#4a5048';
            } else {
                width = Math.max(0.3, 0.6 - (minLevel - 2) * 0.1);
                color = minLevel >= 3 ? '#3a4540' : '#4a5048';
            }
            
            const styleKey = `${edge.id}_${width}_${color}`;
            if (this.previousEdgeStyles[edge.id] !== styleKey) {
                edgeUpdates.push({
                    id: edge.id,
                    width: width,
                    color: { color: color, highlight: '#ffff66' },
                });
                this.previousEdgeStyles[edge.id] = styleKey;
            }
        });
        
        if (edgeUpdates.length > 0) {
            this.updateBatch(edgeUpdates, (batch) => this.data.edges.update(batch));
        }
    }
    
    updateDetails(node, parentIds = []) {
        const detailsDiv = document.getElementById('inner-details');
        if (!detailsDiv || !node) return;
        
        if (node.id === this.breederId) return;
        
        const parentNames = parentIds
            .map(id => {
                const parentNode = this.data.nodes.get(id);
                return parentNode ? `<a href="${parentNode.url || '#'}" class="link">${parentNode.label}</a>` : null;
            })
            .filter(Boolean);
        
        const parentsText = parentNames.length > 0
            ? parentNames.join(' × ')
            : (this.translations.unknown || 'Unknown');
        
        detailsDiv.innerHTML = `
            <h3><a href="${node.url || '#'}" class="link">${node.label}</a>${node.breeder ? ` (${node.breeder})` : ''}</h3>
            <p><strong>${this.translations.parents || 'Parents'}:</strong> ${parentsText}</p>
        `;
    }
}
